'use client';

import { c as _c } from "react/compiler-runtime";
import { type ComponentPropsWithoutRef, type ElementRef, type Ref, useContext } from 'react';
import { OTPInput, OTPInputContext, type RenderProps } from 'input-otp';
import { Dot } from 'lucide-react';
import { cn } from './lib/cn';
const InputOTP = t0 => {
  const $ = _c(14);
  let className;
  let containerClassName;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      containerClassName,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = containerClassName;
    $[3] = props;
    $[4] = ref;
  } else {
    className = $[1];
    containerClassName = $[2];
    props = $[3];
    ref = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("disabled:cursor-not-allowed", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== containerClassName) {
    t2 = cn("flex items-center gap-2 has-disabled:opacity-50", containerClassName);
    $[7] = containerClassName;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== props || $[10] !== ref || $[11] !== t1 || $[12] !== t2) {
    t3 = <OTPInput className={t1} containerClassName={t2} ref={ref} {...props} />;
    $[9] = props;
    $[10] = ref;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
InputOTP.displayName = 'InputOTP';
const InputOTPGroup = t0 => {
  const $ = _c(10);
  let className;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = ref;
  } else {
    className = $[1];
    props = $[2];
    ref = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("flex items-center", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== props || $[7] !== ref || $[8] !== t1) {
    t2 = <div className={t1} ref={ref} {...props} />;
    $[6] = props;
    $[7] = ref;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return t2;
};
InputOTPGroup.displayName = 'InputOTPGroup';
const InputOTPSlot = t0 => {
  const $ = _c(19);
  let className;
  let index;
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      index,
      className,
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = index;
    $[3] = props;
    $[4] = ref;
  } else {
    className = $[1];
    index = $[2];
    props = $[3];
    ref = $[4];
  }
  const inputOTPContext = useContext(OTPInputContext) as RenderProps;
  let t1;
  if ($[5] !== index || $[6] !== inputOTPContext.slots) {
    t1 = inputOTPContext.slots[index] || {};
    $[5] = index;
    $[6] = inputOTPContext.slots;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const {
    char,
    hasFakeCaret,
    isActive
  } = t1;
  const t2 = isActive && "z-10 ring-2 ring-ring ring-offset-background";
  let t3;
  if ($[8] !== className || $[9] !== t2) {
    t3 = cn("relative flex h-10 w-10 items-center justify-center border-gray-medium border-y border-r text-sm transition-all first:rounded-l-full first:border-l last:rounded-r-full", t2, className);
    $[8] = className;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== hasFakeCaret) {
    t4 = hasFakeCaret && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><div className="h-4 w-px animate-caret-blink bg-foreground duration-1000" /></div>;
    $[11] = hasFakeCaret;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== char || $[14] !== props || $[15] !== ref || $[16] !== t3 || $[17] !== t4) {
    t5 = <div className={t3} ref={ref} {...props}>{char}{t4}</div>;
    $[13] = char;
    $[14] = props;
    $[15] = ref;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};
InputOTPSlot.displayName = 'InputOTPSlot';
const InputOTPSeparator = t0 => {
  const $ = _c(7);
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = ref;
  } else {
    props = $[1];
    ref = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Dot />;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props || $[5] !== ref) {
    t2 = <div aria-hidden="true" ref={ref} {...props}>{t1}</div>;
    $[4] = props;
    $[5] = ref;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
InputOTPSeparator.displayName = 'InputOTPSeparator';
export { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot };